import React from 'react';
import PropTypes from 'prop-types';
import FeeInfoTooltip from '../../Common/FeeInfoTooltip';
import { PLATFORM_FEE_INFO, PROCESSING_FEE_INFO } from '../../../utils/bookingPaymentFees';

const CostSummary = ({
  hourlyRate,
  duration,
  cleaningFee,
  processingFee,
  platformFee = 0,
  estimatedTotal,
  venueSubtotal: venueSubtotalProp,
  isDiscounted,
  className,
  title = 'Cost Summary',
  showBreakdown = true,
  discountAmount = 0,
  discountCode = null,
  addOnsTotal = 0,
  addOns = [],
  baseFee: baseFeeProp,
  isPerPerson = false,
  tableCount = null,
  perPersonRate = null,
  usingFlatPricing = false,
  matchingFlatRule = null,
  totalLabel = 'Estimated total',
  discountSlot = null,
  depositQuote = null,
}) => {
  const formattedDuration = duration ? duration.toFixed(1) : '0.0';
  const baseFee = baseFeeProp != null ? baseFeeProp : (hourlyRate || 0) * (duration || 0);
  /** Rate shown next to “× hrs” must multiply to baseFee (avoids £120×1h labelling £65). */
  const displayHourlyRate =
    duration > 0 && baseFee != null && Number.isFinite(baseFee)
      ? baseFee / duration
      : (hourlyRate || 0);
  const formattedHourRate = displayHourlyRate ? displayHourlyRate.toFixed(2) : '0.00';
  const showPerPersonBreakdown = isPerPerson && perPersonRate != null && tableCount != null;
  const hasDiscount = discountAmount > 0 && discountCode;
  const venueSubtotal =
    venueSubtotalProp != null
      ? venueSubtotalProp
      : Math.max(0, baseFee + (addOnsTotal || 0) - (hasDiscount ? discountAmount : 0));
  const showPlatformFee = Number(platformFee) > 0;
  const showProcessingFee = Number(processingFee) > 0;

  return (
    <div className={`cost-summary${className ? ` ${className}` : ''}`}>
      {title ? <h4 className="cost-title">{title}</h4> : null}

      {discountSlot}

      {showBreakdown && (
        <>
          {showPerPersonBreakdown ? (
            <div className="space-fee-with-breakdown">
              <div className="cost-item">
                <span>
                  Space fee
                  {isDiscounted && <span className="discount-indicator"> (Discounted)</span>}
                </span>
                <span>£{baseFee.toFixed(2)}</span>
              </div>
              <p className="space-fee-breakdown">
                (£{Number(perPersonRate).toFixed(2)}/person/hr × {tableCount} people × {formattedDuration} hrs)
              </p>
            </div>
          ) : (
            <div className="cost-item">
              <span>
                {usingFlatPricing
                  ? `Space fee (Flat rate (${matchingFlatRule?.min_hours}+ hours))`
                  : `Space fee (£${formattedHourRate} × ${formattedDuration}hrs)`}
                {isDiscounted && <span className="discount-indicator"> (Discounted)</span>}
              </span>
              <span>£{baseFee.toFixed(2)}</span>
            </div>
          )}
          {addOnsTotal > 0 && (
            <div className="cost-item">
              <span>Items</span>
              <span>£{addOnsTotal.toFixed(2)}</span>
            </div>
          )}
          {addOnsTotal > 0 && addOns.length > 0 && (
            <ul className="cost-addons-list">
              {addOns.map((item) => (
                <li key={item.id || item.name}>
                  <span className="cost-addon-qty">{item.quantity}</span>
                  <span className="cost-addon-times" aria-hidden="true">×</span>
                  <span className="cost-addon-name">{item.name}</span>
                  <span className="cost-addon-price">
                    {Number.isFinite(item.unitPrice) && item.unitPrice > 0
                      ? `£${(item.unitPrice * (item.quantity || 1)).toFixed(2)}`
                      : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {hasDiscount ? (
        <div className="cost-item cost-item--discount">
          <span style={{ color: '#5a9327', fontWeight: '500' }}>
            Discount ({discountCode})
          </span>
          <span style={{ color: '#5a9327', fontWeight: '500' }}>
            -£{discountAmount.toFixed(2)}
          </span>
        </div>
      ) : null}

      <div className="cost-item cost-item--venue-total">
        <span>Venue total</span>
        <span>£{venueSubtotal.toFixed(2)}</span>
      </div>

      {showPlatformFee ? (
        <div className="cost-item">
          <span className="cost-item__label">
            Occasia platform fee
            <FeeInfoTooltip text={PLATFORM_FEE_INFO} label="About Occasia platform fee" />
          </span>
          <span>£{Number(platformFee).toFixed(2)}</span>
        </div>
      ) : null}

      {showProcessingFee ? (
        <div className="cost-item">
          <span className="cost-item__label">
            Processing fee
            <FeeInfoTooltip
              text={PROCESSING_FEE_INFO}
              label="About processing fee"
              iconSrc="/manual-booking/icons/processing-fee-info.png"
            />
          </span>
          <span>£{Number(processingFee).toFixed(2)}</span>
        </div>
      ) : null}

      <div className="cost-total">
        <span>{totalLabel}</span>
        <span>£{estimatedTotal.toFixed(2)}</span>
      </div>

      {depositQuote ? (
        <div className="cost-deposit-schedule">
          <div className="cost-item cost-item--deposit">
            <span>
              Deposit due now ({depositQuote.percent}% of venue total)
            </span>
            <span>£{depositQuote.depositDue.toFixed(2)}</span>
          </div>
          <div className="cost-item cost-item--deposit-balance">
            <span>Balance due after approval</span>
            <span>£{depositQuote.balanceDue.toFixed(2)}</span>
          </div>
          {showProcessingFee ? (
            <p className="cost-deposit-note">
              Card processing fee is collected with your balance payment.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

CostSummary.propTypes = {
  hourlyRate: PropTypes.number,
  duration: PropTypes.number,
  baseFee: PropTypes.number,
  cleaningFee: PropTypes.number.isRequired,
  processingFee: PropTypes.number.isRequired,
  platformFee: PropTypes.number,
  venueSubtotal: PropTypes.number,
  estimatedTotal: PropTypes.number.isRequired,
  isDiscounted: PropTypes.bool,
  className: PropTypes.string,
  title: PropTypes.string,
  showBreakdown: PropTypes.bool,
  discountAmount: PropTypes.number,
  discountCode: PropTypes.string,
  addOnsTotal: PropTypes.number,
  addOns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    quantity: PropTypes.number,
    unitPrice: PropTypes.number
  })),
  isPerPerson: PropTypes.bool,
  tableCount: PropTypes.number,
  perPersonRate: PropTypes.number,
  usingFlatPricing: PropTypes.bool,
  matchingFlatRule: PropTypes.shape({
    min_hours: PropTypes.number,
  }),
  totalLabel: PropTypes.string,
  discountSlot: PropTypes.node,
  depositQuote: PropTypes.shape({
    percent: PropTypes.number.isRequired,
    depositDue: PropTypes.number.isRequired,
    balanceDue: PropTypes.number.isRequired,
  }),
};

export default CostSummary;
